import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import styles from './AboutPage.module.css';
import { useLang } from '../../context/LanguageContext';

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.09, delayChildren: 0.04 } },
};
const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
};

export default function AboutPage() {
  const bodyRef   = useRef(null);
  const skillsRef = useRef(null);
  const bodyInView   = useInView(bodyRef,   { once: true, margin: '-60px' });
  const skillsInView = useInView(skillsRef, { once: true, margin: '-60px' });
  const { t } = useLang();
  const ap = t.aboutPage;

  return (
    <div className={styles.page}>

      {/* ── HERO ── */}
      <section className={styles.hero}>
        <div className={styles.heroBg}>
          <div className={styles.gridLines} />
          <div className={styles.glow} />
        </div>
        <div className={styles.heroInner}>
          <motion.p className={styles.eyebrow}
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.38 }}>
            {ap.eyebrow}
          </motion.p>
          <motion.h1 className={styles.heroTitle}
            initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1], delay: 0.07 }}>
            {ap.heroTitle}{' '}<span className={styles.accent}>{ap.heroAccent}</span>
          </motion.h1>
          <motion.p className={styles.location}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.28 }}>
            <span className={styles.locationDot} />
            {ap.location}
          </motion.p>
        </div>
      </section>

      {/* ── MAIN — 2 colonnes ── */}
      <section className={styles.main}>
        <div className={styles.container}>
          <div className={styles.grid}>

            {/* LEFT — texte */}
            <motion.div className={styles.textCol}
              ref={bodyRef} variants={stagger}
              initial="hidden" animate={bodyInView ? 'visible' : 'hidden'}>

              <motion.p className={styles.bodyLead} variants={fadeUp}>
                {t.about.title}
              </motion.p>

              <motion.p className={styles.body} variants={fadeUp}>
                {t.about.body1}
              </motion.p>

              <motion.p className={styles.body} variants={fadeUp}>
                {t.about.body2}
              </motion.p>

              {/* CTA */}
              <motion.div className={styles.ctas} variants={fadeUp}>
                <Link to="/contact" className={styles.ctaPrimary}>
                  {ap.cta1}
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                </Link>
                <Link to="/methode" className={styles.ctaGhost}>{ap.cta2}</Link>
              </motion.div>
            </motion.div>

            {/* RIGHT — visual */}
            <motion.div className={styles.visual}
              initial={{ opacity: 0, scale: 0.96, filter: 'blur(8px)' }}
              animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}>

              {/* Avatar card */}
              <div className={styles.avatarCard}>
                <div className={styles.avatarGlow} />
                <div className={styles.avatarInner}>
                  <img src="/assets/photo.jpg" alt="Islam B." className={styles.avatarPhoto} />
                </div>
                <div className={styles.avatarName}>{ap.name}</div>
                <div className={styles.avatarRole}>{ap.role}</div>
              </div>

              {/* Skills */}
              <motion.div className={styles.skillsWrap}
                ref={skillsRef} variants={stagger}
                initial="hidden" animate={skillsInView ? 'visible' : 'hidden'}>
                <p className={styles.skillsLabel}>{t.about.skills}</p>
                <div className={styles.skills}>
                  {ap.skills.map((s) => (
                    <motion.span key={s} className={styles.skill} variants={fadeUp}>
                      {s}
                    </motion.span>
                  ))}
                </div>
              </motion.div>
            </motion.div>

          </div>
        </div>
      </section>

    </div>
  );
}